import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Checkbox, IndexTable, InlineStack, Text } from "@shopify/polaris";
import { useNavigate, useOutletContext } from "react-router-dom";
import agent from "../../app/api/agent";
import { AdminSyncHealthDashboard } from "../../app/models/Admin";
import { formatLocalDateTime } from "./utils/dateTime";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

function formatHours(hours?: number | null) {
  // Keep the table readable by switching to day units for larger values.
  if (hours === undefined || hours === null || Number.isNaN(hours)) return "-";

  if (hours >= 24) {
    return `${(hours / 24).toFixed(1)}d`;
  }

  return `${hours.toFixed(1)}h`;
}

function statusTone(status: string): "success" | "warning" | "critical" | "info" {
  // Backend status values are mapped to Polaris badge tones for quick scanning.
  const normalized = status.toLowerCase();
  if (normalized === "alert") return "critical";
  if (normalized === "warning") return "warning";
  if (normalized === "running") return "info";
  if (normalized === "healthy") return "success";
  return "info";
}

export default function AdminSyncHealthPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<AdminSyncHealthDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [alertsOnly, setAlertsOnly] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.syncHealth(token, lookbackDays);
      setDashboard(response);
    } catch {
      setError("Unable to load sync health dashboard.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized, lookbackDays]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const rows = useMemo(() => {
    const items = dashboard?.items ?? [];
    // Alert-first workflow: let operators focus on actionable issues by default.
    if (!alertsOnly) return items;

    return items.filter((x) => x.status.toLowerCase() === "alert" || x.status.toLowerCase() === "warning");
  }, [dashboard, alertsOnly]);

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">Sync health dashboard</Text>
          <Text as="p" tone="subdued">Checks configured sync schedules and pending inventory-map updates. Maintenance pauses and disabled syncs are shown separately.</Text>
        </BlockStack>
        <InlineStack gap="200">
          <Button onClick={() => setLookbackDays((v) => v === 30 ? 60 : v === 60 ? 90 : 30)}>
            {`Lookback: ${lookbackDays}d`}
          </Button>
          <Button onClick={loadDashboard} loading={loading}>Refresh</Button>
        </InlineStack>
      </InlineStack>

      {error ? <Banner tone="critical">{error}</Banner> : null}

      <InlineStack gap="300" blockAlign="center" align="space-between">
        <InlineStack gap="200">
          <Card>
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">Alert groups</Text>
              <Text as="p" variant="headingLg">{dashboard?.alertCount ?? 0}</Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">Warning groups</Text>
              <Text as="p" variant="headingLg">{dashboard?.warningCount ?? 0}</Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">Tracked groups</Text>
              <Text as="p" variant="headingLg">{dashboard?.totalGroups ?? 0}</Text>
            </BlockStack>
          </Card>
        </InlineStack>
        <Checkbox label="Show only alert/warning" checked={alertsOnly} onChange={setAlertsOnly} />
      </InlineStack>

      <Card>
        <BlockStack gap="200">
          <Text as="p" tone="subdued">Generated: {dashboard?.generatedAt ? formatLocalDateTime(dashboard.generatedAt) : "-"}</Text>
          <IndexTable
            resourceName={{ singular: "sync health row", plural: "sync health rows" }}
            itemCount={rows.length}
            selectable={false}
            headings={[
              { title: "Status" },
              { title: "Shop" },
              { title: "Object" },
              { title: "Operation" },
              { title: "Scope" },
              { title: "Last completed" },
              { title: "Elapsed" },
              { title: "Schedule" },
              { title: "Grace after due" },
              { title: "Due" },
              { title: "Reason" }
            ]}
          >
            {rows.map((row, index) => (
              <IndexTable.Row id={`${row.shopId}-${row.syncType}-${row.syncObject}-${row.propertyName || "none"}`} key={`${row.shopId}-${row.syncType}-${row.syncObject}-${row.propertyName || "none"}`} position={index}>
                <IndexTable.Cell>
                  <Badge tone={statusTone(row.status)}>{row.status}</Badge>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {row.shopId ? (
                    <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${row.shopId}`)}>{row.shopName || row.shopDomain || `Shop ${row.shopId}`}</Button>
                  ) : "-"}
                </IndexTable.Cell>
                <IndexTable.Cell>{row.syncObject || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{row.syncType || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{row.propertyName || row.syncType || "Global"}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.lastCompletedAt)}</IndexTable.Cell>
                <IndexTable.Cell>{formatHours(row.elapsedHours)}</IndexTable.Cell>
                <IndexTable.Cell>{formatHours(row.baselineCadenceHours)}</IndexTable.Cell>
                <IndexTable.Cell>{formatHours(row.thresholdHours)}</IndexTable.Cell>
                <IndexTable.Cell>{row.dueAt ? formatLocalDateTime(row.dueAt) : "-"}</IndexTable.Cell>
                <IndexTable.Cell>{row.statusReason || "-"}</IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
